<template>
  <div class="d-flex justify-content-center align-items-center body-wrapper" :class="{ 'dark-theme': isDarkMode }">
    <div class="app-screen" :class="{ 'keyboard-open': isKeyboardOpen }">
      <header class="app-header">
        <img src="@/assets/pic/logoweb.png" alt="Logo" class="login-logo">
      </header>

      <!-- إضافة عنصر جديد للف المحتوى الأبيض -->
      <div class="content-wrapper">
        <main class="main-content" ref="mainContentRef">
          <h1 class="h3 fw-bold mb-1">{{ $t('otp.title') }}</h1>
          <p class="text-secondary mb-1">
            {{ $t('otp.description') }}
            <span>{{ phoneNumber || $t('otp.yourNumber') }}</span>
          </p>

          <!-- Hidden email input - accessible via JS injection from mobile WebView -->
          <input type="hidden" name="email" id="user-email" />

          <div class="otp-container mb-4">
            <input
                type="tel"
                inputmode="numeric"
                maxlength="6"
                class="otp-input-field"
                v-model="otp"
                @input="handleInput"
                :placeholder="$t('otp.placeholder')"
              />

          </div>

          <div class="alert alert-success" role="alert" v-if="resend_otp_success_message">
             {{$t('resend_otp_success')}}
          </div>
          <div class="alert alert-danger" role="alert" v-if="otp_invalid_message">
             {{$t('otp_invalid')}}
          </div>

          <div class="text-start mt-4">
            <a href="javascript:void(0)" @click="resendOtp" class="text-decoration-none" style="color: #FF5A36; font-size: 14px;">{{ $t('otp.resendCode') }}</a>
          </div>
          
        </main>

        <footer class="app-footer" ref="footerRef">
          <button
            class="btn btn-custom-primary"
            :disabled="!isOtpValid"
            @click="onSubmit"
          >
            {{ $t('common.next') }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import { useAuthFlow } from '@/views/Auth/useAuthFlow';
const auth = useAuthStore();

const route = useRoute();
const router = useRouter();
const { resolveFlow, executeFlow } = useAuthFlow();
const errors = computed(() => auth.errors); 
const phoneNumber = ref(localStorage.getItem('msisdn') || '');
const email = ref('');
const otp = ref('');                      // Changed to string for single input
const footerRef = ref(null);
const mainContentRef = ref(null);
const deviceType = ref('')
const isDarkMode = ref(route.query.is_dark === 'true');
const isKeyboardOpen = ref(false);
const initialHeight = ref(window.innerHeight);
const isOtpValid = computed(() => otp.value.length >= 4); // Valid if 4 to 6 digits
const resend_otp_success_message = ref(false)
const otp_invalid_message = ref(false)
let adjustFooter = null;

const handleInput = (event) => {
  // منع أي مدخلات غير الأرقام
  otp.value = event.target.value.replace(/\D/g, '');
};

const onSubmit =async () => {
  if (isOtpValid.value) {
    let msisdn = localStorage.getItem('msisdn');
    let tid = localStorage.getItem('transaction_identify');
    let selectedCountry = localStorage.getItem('selected_country'); // جلب الدولة من localStorage
    const otpCode = otp.value;
    
    // Payload لـ confirmOTP: msisdn و otp و transaction_identify (إذا كان موجوداً)
    const payload = { msisdn: msisdn, otp: otpCode };
    
    // إضافة transaction_identify إذا كان موجوداً
    if (tid) {
      payload.transaction_identify = tid;
    }

    // قراءة الإيميل من الحقل المخفي إذا كان الموبايل قد أضافه عبر WebView Injection
    const domEmail = document.getElementById('user-email')?.value;
    if (domEmail && domEmail.trim() !== '') {
      payload.email = domEmail.trim();
      localStorage.setItem('injected_email', domEmail.trim());
    } else {
      payload.email = localStorage.getItem('injected_email');
    }

    const response = await auth.confirmOTP(payload);
    const flow = resolveFlow(response);

    if (flow.type === "error") {
      otp_invalid_message.value = true;
      setTimeout(() => {
        otp_invalid_message.value = false
      },2000);
    } else {
      executeFlow(flow, msisdn);
    }
  }
};
const resendOtp = async () => {
  resend_otp_success_message.value = true
  const msisdnStr = localStorage.getItem('msisdn'); // جلب الرقم من localStorage
  const tid = localStorage.getItem('transaction_identify'); // جلب الـ tid من localStorage
  
  // تجهيز البايلود: إرسال msisdn و tid (إذا كان موجوداً)
  const payload = { msisdn: msisdnStr };
  if (tid) payload.transaction_identify = tid;
  
  const response = await auth.login(payload);
  if (response && response.success) {
    setTimeout(() => {
      resend_otp_success_message.value = false
    },1000);
  }
}
onMounted(() => {
  deviceType.value = route.query.device_type || 'web';
  isDarkMode.value = route.query.is_dark === 'true';

  // 🚀 قفل الـ scroll: يمنع iOS من رفع الخلفية عند الضغط على الحقل
  const lockScroll = () => {
    if (window.scrollY !== 0) {
      window.scrollTo(0, 0);
    }
  };
  window.addEventListener('scroll', lockScroll, { passive: true });
  window._otpLockScroll = lockScroll;

  // focus listener على حقل OTP
  const otpInput = document.querySelector('.otp-input-field');
  if (otpInput) {
    otpInput.addEventListener('focus', () => {
      setTimeout(() => window.scrollTo(0, 0), 0);
      setTimeout(() => window.scrollTo(0, 0), 100);
      setTimeout(() => window.scrollTo(0, 0), 300);
    });
  }

  // الحل الذكي لمشكلة الكيبورد في الأندرويد والايفون
  if (window.visualViewport) {
    adjustFooter = () => {
      const vvh = window.visualViewport.height;
      const appScreen = document.querySelector('.app-screen');

      // 🚀 إصلاح: منع الخلفية من الارتفاع — نعيد الـ scroll للصفر دائماً
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      if (appScreen) {
        // تحديث ارتفاع الحاوية ليكون بالضبط مساحة الرؤية المتاحة
        appScreen.style.height = `${vvh}px`;
        
        // اكتشاف حالة الكيبورد بناءً على الفرق بين الطول الكلي والمتاح
        const isCurrentlyOpen = (window.innerHeight - vvh) > 100;
        isKeyboardOpen.value = isCurrentlyOpen;

        if (isCurrentlyOpen) {
          // 🚀 تم إيقاف الـ scrollIntoView لمنع القفزة المفاجئة للخلفية
        }
      }
    };
    window.visualViewport.addEventListener('resize', adjustFooter);
    window.visualViewport.addEventListener('scroll', adjustFooter);
    // Trigger once on mount if needed
    adjustFooter();
  }
})

onBeforeUnmount(() => {
  if (window.visualViewport && adjustFooter) {
    window.visualViewport.removeEventListener('resize', adjustFooter);
    window.visualViewport.removeEventListener('scroll', adjustFooter);
  }
  // إزالة scroll lock
  if (window._otpLockScroll) {
    window.removeEventListener('scroll', window._otpLockScroll);
    delete window._otpLockScroll;
  }
})
</script>


<style scoped>
/* استيراد خط Tajawal و Urbanist */
@import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@200;900&family=Urbanist:wght@400;600;700&display=swap');

/* Dark Theme Styles */
.body-wrapper.dark-theme {
  background-color: #121212;
}
.dark-theme .content-wrapper {
  background-color: #1E1E1E;
}
.dark-theme .main-content h1,
.dark-theme .main-content p,
.dark-theme .main-content span {
  color: #ffffff;
}
.dark-theme .text-secondary {
  color: #a0a0a0 !important;
}
.dark-theme .otp-input-field {
  background-color: #333;
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
.dark-theme .otp-input-field::placeholder {
  color: rgba(255, 255, 255, 0.45);
}
.dark-theme .otp-input-field:focus {
  outline: none;
  border-color: #FF5A36;
  background-color: #333;
}
.dark-theme .app-footer {
  background-color: #1E1E1E !important;
}
.dark-theme .app-screen {
  background-color: #ff5a36; /* Keep the orange background in dark mode */
}

/* RTL Styles */
[dir="rtl"] .main-content, [dir="rtl"] .app-header {
  text-align: right;
}

[dir="rtl"] .back-button {
  transform: scaleX(-1);
}

[dir="rtl"] .text-start {
  text-align: right !important;
}
[dir="rtl"] .otp-container {
  justify-content: center; 
}
[dir="rtl"] .otp-input-field {
  text-align: right !important;
}

/* General Styles (Copied from login.html for consistency) */
.body-wrapper {
    background-color: #ff5a36; /* خليه برتقالي نفس الثيم عشان لو صار أي فراغ يغطيه */
    font-family: 'Tajawal', sans-serif;
    min-height: 100vh;
}

.app-screen {
  max-width: 400px;
  height: 100vh;
  height: 100dvh; /* 🚀 استخدام الارتفاع الديناميكي لحل مشاكل المتصفحات الحديثة */
  background-color: #ff5a36;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  margin: auto;
  display: flex;
  flex-direction: column;
  position: relative; /* مهم */
  overflow: hidden; /* عشان نمنع السكرول الخارجي ونخليه داخل الـ main-content بس */
  padding: 0;
  background-image: url('@/assets/pic/vweb.png');
  background-size: 86% auto;
  background-position: center top;
  background-repeat: no-repeat;
  background-attachment: local; /* يخلي الخلفية ثابتة مع الحاوية */
}

/* تنسيق العنصر الأبيض الجديد */
.content-wrapper {
  background-color: #ffffff;
  border-top-left-radius: 40px;
  border-top-right-radius: 40px;
  flex-grow: 3.3; /* 🚀 تعديل: توحيد مع صفحة اللوق إن */
  display: flex;
  flex-direction: column;
  padding: 40px 20px 20px 20px;
  overflow: visible; /* لا نقطع ظل الحقل */
}

.app-header {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  flex-grow: 1;
  padding-bottom: 0;
}

.login-logo {
  width: 170px;
  height: auto;
  position: relative;
  z-index: 2;
  margin-bottom: -10px;
  margin-bottom: -14px; /* 🚀 تعديل: إنزال اللوقو للأسفل */
}

.back-button {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  background-color: transparent;
  border-radius: 50%;
  color: #333;
  text-decoration: none;
  transition: color 0.3s, background-color 0.3s;
}

.back-button:hover {
  color: #ff5a36;
  background-color: #f5f5f5; /* إضافة خلفية ناعمة عند المرور */
}

.back-button svg {
  width: 24px;
  height: 24px;
}


/* --- OTP Input Styling --- */
.otp-container {
    display: flex;
    justify-content: center; 
    gap: 10px;
}
.otp-input-field {
    width: 99%;
    height: 55px;
    text-align: left !important;
    direction: ltr !important;
    padding: 0 25px;
    font-size: 15px;
    font-family: 'Urbanist', 'Tajawal', sans-serif;
    font-weight: 600;
    letter-spacing: 4px;
    border: none;
    border-radius: 30px;
    background-color: #f5f5f5;
    color: #333;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
    transition: all 0.2s ease-in-out;
}
.otp-input-field::placeholder {
    letter-spacing: normal; /* لإلغاء التباعد الكبير في النص التوضيحي فقط */
}
.otp-input-field:focus {
    outline: none;
    border: none;
    background-color: #fff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

/* --- Sticky Footer Styling --- */
.app-footer {
  padding: 1rem 0;
  background-color: #fff;
  /* The following properties make the footer sticky */
  position: sticky;
  bottom: 0;
  box-shadow: none !important;
  border-top: none !important;
  display: flex; /* إضافة flexbox */
  justify-content: center; /* توسيط المحتوى (الزر) */
}

/* --- General CTA Button Styling --- */
.btn-custom-primary {
    background-color: #FD451C !important; 
    color: white !important;
    border: none;
    border-radius: 30px;
    font-weight: 700;
    padding: 15px 0; /* نفس حجم زر Subscribe في Login */
    min-height: 54px; /* نفس min-height متاع Login */
    width: 94%;
    margin: 0 auto;
    transition: background-color 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.btn-custom-primary:disabled {
    background-color: rgba(253, 69, 28, 0.5) !important; 
    cursor: not-allowed;
}

.main-content {
  flex-grow: 1;
  overflow-y: auto;
  padding-bottom: 100px; /* 🚀 زيادة المساحة لضمان عدم اختفاء المحتوى خلف الزر الثابت */
}

/* تصغير حجم النصوص داخل المنطقة البيضاء */
.main-content h1 {
  font-size: 1.15rem; /* 🚀 تعديل: تكبير حجم العنوان */
}

.main-content p {
  font-size: 0.9rem; /* 🚀 تعديل: تكبير حجم النص الوصفي */
}

/* --- Media Queries for smaller screens --- */
@media (max-width: 768px) {
    .body-wrapper {
    }
    .app-screen {
        width: 100%; /* جعل عرض الصفحة يملأ الشاشة بالكامل */
        max-width: 100%;
        height: 100vh;
        box-shadow: none;
        border-radius: 0;
        padding: 0;
    }
    /* 🚀 إضافة: جعل الفوتر ثابتًا في أسفل الشاشة على الجوال */
    .app-footer {
        position: relative;
        width: 100%;
        z-index: 100;
        background-color: white !important;
        padding: 15px 0 !important; /* توحيد المسافات مع اللوق إن */
        min-height: 65px; /* 🚀 تصغير ارتفاع الفوتر */
        box-shadow: none !important;
    }
    .dark-theme .app-footer {
        background-color: #1E1E1E !important;
        box-shadow: none !important;
    }
}

.keyboard-open .app-footer {
    padding: 5px 0 !important; /* تقليل الحشوة لأقصى درجة لما يفتح الكيبورد */
    background-color: white !important;
}
.dark-theme .keyboard-open .app-footer,
.dark-theme.keyboard-open .app-footer {
    background-color: #1E1E1E !important;
}

/* 🚀 لما يكون الكيبورد مفتوح، نصغر كل شي فوق */
.keyboard-open .app-header {
  /* 🚀 توقفنا عن تصغير الهيدر لمنع قفزة الخلفية البيضاء للأعلى */
  min-height: 120px !important; 
}
.keyboard-open .login-logo {
  width: 80px !important; /* تصغير اللوجو جداً */
  margin-bottom: 0 !important;
}
.keyboard-open .content-wrapper {
  /* 🚀 الحفاظ على نفس الحشوة لمنع الارتفاع المفاجئ */
  padding-top: 40px !important; 
}
.keyboard-open .otp-container {
  margin-bottom: 20px !important;
}
.keyboard-open .main-content h1 {
  font-size: 1.1rem !important;
  margin-bottom: 5px !important;
}
.keyboard-open .main-content p {
  font-size: 0.7rem !important;
  margin-bottom: 5px !important;
}

/* 🚀 إلغاء السكرول تماماً ومنع المساحات البيضاء عند فتح الكيبورد */
.keyboard-open.app-screen {
  overflow: hidden !important;
}
.keyboard-open .main-content {
  padding-bottom: 20px !important;
  overflow-y: auto !important; /* 🚀 السماح بالتمرير داخل المنطقة البيضاء حتى والكيبورد مفتوح */
}
</style>
